import { supabase } from '@/lib/supabase';
import {
  tg, canShareToStory, inviteLink, uploadCard, shareCardWithFriend, shareImageFile, downloadImage,
} from '@/utils/shareCard';
import {
  W, SAFE_TOP, SAFE_BOTTOM, EMERALD, INK, INK_MUTED, INK_DIM, BOT_USERNAME,
  wrap, newCard, drawDofi, drawFooter, toPng,
} from '@/utils/cardCanvas';
import { renderWeekCard } from '@/utils/weekCard';
import { getWeekRation, type WeekRation } from '@/lib/weekRation';

// ════════════════════════════════════════════════════════════════
// Карточка достижения — картинкой, чтобы выложить в историю или отправить.
//
// Замысел Славы с самого начала: достижение — это маркетинг. Человек достиг,
// карточка ушла в соцсети, друг перешёл по ссылке. До 03.09.2026 не работало:
// модалка показывала «+100 XP» (внутренняя валюта, ничего не значит снаружи),
// а репост был скриншотом. Здесь карточка рисуется в бренде, несёт цифру,
// фразу Славы и ссылку с реферальным кодом — тем самым, что лежал в Профиле
// с нулём использований.
//
// Рисуем на канвасе, как стих (poemCard.ts): та же машинка, другой шаблон.
// ════════════════════════════════════════════════════════════════

export { BOT_USERNAME, canShareToStory, inviteLink, uploadCard };
export type ShareChannel = 'story' | 'chat' | 'image';

export type ShareableAchievement = {
  id: string;
  title: string;
  icon?: string | null;
  slava_line?: string | null;
  target_value?: number | null;
  category?: string | null;
};

/** Крупная цифра карточки: серия в днях, килограммы, число записей */
function bigNumber(a: ShareableAchievement): { value: string; unit: string } | null {
  const n = a.target_value ?? 0;
  if (a.id.startsWith('streak_')) return { value: String(n), unit: n === 1 ? 'день подряд' : n < 5 ? 'дня подряд' : 'дней подряд' };
  if (a.id.startsWith('weight_lose_')) return { value: `−${n}`, unit: n === 1 ? 'килограмм' : n < 5 ? 'килограмма' : 'килограммов' };
  if (a.id.startsWith('meals_')) return { value: String(n), unit: 'записей о еде' };
  if (a.id === 'days_100') return { value: '100', unit: 'дней с Дофи' };
  // Неделя в дневнике (29.09.2026): крупно — серия, потому что именно она
  // и есть повод. Нутриенты на карточку не выносим, см. RationWeekCard.
  if (a.id === 'ration_week') return { value: String(n), unit: 'дней подряд с дневником' };
  if (a.id === 'course_done') return { value: '21', unit: 'день Пути' };
  if (a.id.startsWith('photo_')) return { value: String(n), unit: 'блюд по фото' };
  // Вехи привычек (22.09.2026): число дней, когда получилось, — не обязательно подряд
  if (a.id.startsWith('habit_')) return { value: String(n), unit: n % 10 === 1 && n % 100 !== 11 ? 'день с привычкой' : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 'дня с привычкой' : 'дней с привычкой' };
  return null;
}

export async function renderAchievementCard(
  a: ShareableAchievement,
  opts: { name?: string | null; refCode?: string | null } = {},
): Promise<Blob> {
  const { canvas, ctx } = await newCard();

  // ── Дофи со значком достижения — как в окне, которое человек только что видел.
  // До 07.10.2026 здесь был один смайлик на 150px.
  const big = bigNumber(a);
  const isBigWin = (a.target_value ?? 0) >= 30 || /finish|path_done|days_100|course_done/.test(a.id);
  await drawDofi(ctx, { cy: SAFE_TOP + 200, size: 340, pose: isBigWin ? 'proud' : 'joy', badge: a.icon });

  // ── Цифра или название ──
  let y = SAFE_TOP + 490;
  ctx.textAlign = 'center';
  if (big) {
    ctx.fillStyle = EMERALD;
    ctx.font = '700 200px "Space Grotesk", Inter, system-ui, sans-serif';
    ctx.shadowColor = 'rgba(16,229,142,0.45)';
    ctx.shadowBlur = 40;
    ctx.fillText(big.value, W / 2, y + 60);
    ctx.shadowBlur = 0;
    y += 130;
    ctx.fillStyle = INK;
    ctx.font = '500 44px Inter, system-ui, sans-serif';
    ctx.fillText(big.unit, W / 2, y);
    y += 90;
  } else {
    ctx.fillStyle = INK;
    ctx.font = '700 72px "Space Grotesk", Inter, system-ui, sans-serif';
    for (const line of wrap(ctx, a.title, W - 200)) { ctx.fillText(line, W / 2, y); y += 84; }
    y += 30;
  }

  // ── Фраза Дофи — самое человеческое на карточке ──
  if (a.slava_line) {
    ctx.fillStyle = INK_MUTED;
    ctx.font = 'italic 400 38px Georgia, "Times New Roman", serif';
    // Не больше трёх строк: имя и подвал ниже идут тем же курсором,
    // длинная фраза столкнула бы ссылку под интерфейс сторис.
    const lines = wrap(ctx, `«${a.slava_line}»`, W - 260).slice(0, 3);
    for (const line of lines) { ctx.fillText(line, W / 2, y); y += 56; }
    ctx.fillStyle = INK_DIM;
    ctx.font = '400 26px Inter, system-ui, sans-serif';
    ctx.fillText('— Дофи', W / 2, y + 12);
  }

  // ── Имя и подвал: тем же потоком сверху вниз, одним курсором ──
  y = Math.max(y + 120, SAFE_BOTTOM - 360);
  if (opts.name) {
    ctx.fillStyle = INK;
    ctx.font = '600 40px Inter, system-ui, sans-serif';
    ctx.fillText(opts.name, W / 2, y);
  }
  drawFooter(ctx, y + 120, opts.refCode);
  return toPng(canvas);
}

/**
 * Один тап — один канал. Считаем каждый репост в share_events: без этого
 * через месяц снова будем гадать, работает ли механика.
 */
export async function shareAchievement(
  channel: ShareChannel,
  a: ShareableAchievement,
  ctx: { userId: string; name?: string | null; refCode?: string | null; week?: WeekRation | null },
): Promise<void> {
  // Неделя в дневнике уходит наружу самим разбором — витамины, радуга, Дофи
  // (просьба Славы 07.10.2026: «не листик, а именно этот разбор»).
  // Разбор не собрался — обычная карточка достижения, репост не срывается.
  const week = a.id === 'ration_week' ? (ctx.week ?? await getWeekRation()) : null;
  const blob = week && week.daysFull > 0
    ? await renderWeekCard(week, { name: ctx.name, refCode: ctx.refCode })
    : await renderAchievementCard(a, { name: ctx.name, refCode: ctx.refCode });
  const link = inviteLink(ctx.refCode);
  const text = `${a.title} в Slava Health`;

  if (channel === 'story') {
    const url = await uploadCard(blob, ctx.userId, a.id);
    tg().shareToStory(url, {
      text: `${text} — ${link}`,
      widget_link: { url: link, name: 'Slava Health' },
    });
  } else if (channel === 'chat') {
    // Карточкой, а не текстом — везде (Слава 07.10.2026). Порядок попыток в shareCard.ts.
    await shareCardWithFriend(blob, {
      userId: ctx.userId,
      key: a.id,
      caption: `${text}. ${a.slava_line ?? ''}`.trim(),
      link,
      fileName: `slava-${a.id}.png`,
    });
  } else {
    const file = `slava-${a.id}.png`;
    if (!(await shareImageFile(blob, file, `${text} ${link}`))) downloadImage(blob, file);
  }

  supabase.from('share_events').insert({ user_id: ctx.userId, achievement_id: a.id, channel }).then(() => {});
}
